import {
  ActionIcon,
  Alert,
  Autocomplete,
  Button,
  Card,
  Grid,
  Group,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconArrowRight, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ALL_CONVERSION_EVENTS,
  ANALYTICS_CONVERSION_EVENTS,
  CONVERSION_EVENTS,
} from '../../../constants/conversionEvents';
import eventTypes from '../../../constants/eventTypes';
import { actionsForConversionType, conversionTypesOf } from '../../../helpers/customEventConversions';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import Spinner from '../../Spinner';
import SuperAdminArea from '../../SuperAdmin/SuperAdminArea';

const CHANNEL_OPTIONS = [
  { value: 'global', label: 'All Channels' },
  { value: 'email', label: 'Email' },
  { value: 'textMessage', label: 'SMS' },
  { value: 'viberMessage', label: 'Viber' },
  { value: 'pushNotification', label: 'Push' },
  { value: 'whatsappMessage', label: 'WhatsApp' },
  { value: 'facebookAd', label: 'Facebook Ad' },
  { value: 'googleAd', label: 'Google Ad' },
];

const AD_CHANNELS = ['facebookAd', 'googleAd'];

const MERGE_TAG_OPTIONS = [
  '{{profileId}}',
  '{{campaignId}}',
  '{{messageId}}',
  '{{messageName}}',
  '{{segmentId}}',
  '{{domainId}}',
  '{{group}}',
];

const AD_MERGE_TAG_OPTIONS = ['{{campaignId}}', '{{campaignName}}', '{{messageId}}', '{{messageName}}'];

// Actions the backend remaps to standard funnel events on its own
// (STANDARD_CUSTOM_EVENT_MAPPINGS wins before any conversion mapping), so they
// can't be given a conversion type here.
const UNMAPPABLE_ACTIONS = ['checkoutInitiate', 'addPaymentInfo'];

// Standard commerce actions always offered in the add picker, so an operator
// can (re)add them as rows even if they were removed from the conversions map.
const STANDARD_ACTIONS = ['cartCheckoutSuccess', 'cartAdd', 'cartCreate'];

const AD_TYPE_VALUES = new Set(CONVERSION_EVENTS.map((e) => e.value));
const ANALYTICS_TYPE_VALUES = ANALYTICS_CONVERSION_EVENTS.map((e) => e.value);

// A Releva mapping implies a social-media mapping on its own: the backend
// forwards purchase-classified events as the platforms' purchase event and
// engagement-classified ones as add-to-cart, driven by the commerce type in the
// entry itself. Purchase is locked — the backend always sends Purchase and
// ignores any ad-conversion override for it; Engagement is a default that an
// explicit ad conversion can replace (Meta only).
const IMPLIED_SOCIAL_MAPPINGS = {
  Purchase: {
    value: 'Purchase',
    label: 'Purchase (auto)',
    description:
      'Sent automatically as the purchase event — Meta Purchase, Google purchase. Enable it per platform in its settings.',
    locked: true,
  },
  AddToCart: {
    value: 'AddToCart',
    label: 'Add to cart (auto)',
    description:
      'Sent automatically as the add-to-cart event — Meta AddToCart, Google add_to_cart. Pick an ad conversion to send that instead (Meta).',
    locked: false,
  },
};

const OPTION_META = [...ALL_CONVERSION_EVENTS, ...Object.values(IMPLIED_SOCIAL_MAPPINGS)];

const SettingsTabAnalytics = ({ domain, setDomain, handleChange, eventActions, isSuperAdmin }) => {
  const fetch = useFetch();
  const currentAccount = useSelector((state) => state.account.account);

  // commenting out logo related elements, as we will not be generating them currently
  // not removing them because we will use them in another tool

  // One unified "Conversions" pipeline. Each row reads left to right the way an
  // event flows through the system:
  //   event (standard / custom) → Releva mapping (how Reports & Analytics count
  //   it) → social media mapping (the ad conversion it's forwarded as) →
  //   optional fixed value used when the event carries no products/value of its
  //   own. All of it lives in customEventConversions, the single source of
  //   truth since the purchaseEvents/cartAddEvents/cartCreateEvents domain
  //   arrays were retired (magellan-api #2693-#2696).
  // Currency: the value is always in the domain currency; ad platforms convert
  // it into their own reporting currency automatically (RN-3635).
  const [draftActions, setDraftActions] = useState([]);

  // High-frequency trigger events — a super-admin-only section. The fetch is
  // gated on the role for the same reason the section is: a regular admin never
  // sees it, so it must not cost them a request.
  const [selectedEvent, setSelectedEvent] = useState('');
  const [notAllowedEventsForTrigger, setNotAllowedEventsForTrigger] = useState([]);
  // Initialised from isSuperAdmin, not a bare `true`: the effect below returns
  // early for a non-super-admin, so a bare `true` would sit permanently — safe
  // today only because the consuming block is also gated behind
  // SuperAdminArea, but that makes the two guards load-bearing together and
  // the non-super-admin test unable to tell "not loading" from "not rendered".
  const [triggerEventsLoading, setTriggerEventsLoading] = useState(isSuperAdmin);
  const [triggerEventsError, setTriggerEventsError] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    setTriggerEventsLoading(true);
    setTriggerEventsError(null);

    fetch(`/api/admin/accounts/${currentAccount.id}/domains/${domain.id}/eventActions?notAllowedEventsForTrigger=true`)
      .then((res) => {
        setNotAllowedEventsForTrigger(res.eventActions || []);
        setTriggerEventsLoading(false);
      })
      .catch((err) => {
        setTriggerEventsError(err.message || 'Failed to load high-frequency events');
        setTriggerEventsLoading(false);
      });
  }, [fetch, currentAccount.id, domain.id, isSuperAdmin]);

  // Every read of customEventConversions goes through the helpers because an
  // entry may be stored as either { event } or { events: [...] } (magellan-api
  // #2694 migrates to the latter). Writes (commitRows below) always emit the
  // { events: [...] } shape, which is the only one that can carry a row's
  // commerce types and ad type at once. One consequence that is easy to miss:
  // a stored entry carrying more types than the row model can express (e.g. two
  // ad types) is collapsed to its commerce types plus a single ad type on any
  // commit, including one that never touched that row.
  const rows = useMemo(() => {
    const byAction = {};
    const ensure = (action) => (byAction[action] ||= { action, relevaTypes: [], adType: null, value: undefined });
    ANALYTICS_TYPE_VALUES.forEach((type) => {
      actionsForConversionType(domain.customEventConversions, type).forEach((a) => ensure(a).relevaTypes.push(type));
    });
    Object.entries(domain.customEventConversions || {}).forEach(([action, cfg]) => {
      const row = ensure(action);
      const adType = conversionTypesOf(cfg).find((type) => AD_TYPE_VALUES.has(type));
      if (adType) row.adType = adType;
      if (cfg?.value != null) row.value = cfg.value;
    });
    draftActions.forEach((a) => ensure(a));
    return Object.values(byAction).sort((x, y) => x.action.localeCompare(y.action));
  }, [domain.customEventConversions, draftActions]);

  // Rebuild customEventConversions from the table and commit it atomically. A
  // row's Releva mapping and its ad type are all types of the same entry: the
  // commerce ones classify it for Reports & Analytics, the ad one is what it's
  // forwarded as. A row with no type at all is not persisted.
  const commitRows = (nextRows) => {
    const derived = {};
    for (const row of nextRows) {
      const events = [...row.relevaTypes, ...(row.adType ? [row.adType] : [])];
      if (events.length) {
        derived[row.action] = { events, ...(row.value != null ? { value: row.value } : {}) };
      }
    }
    // Functional update so a same-tick edit to another domain field can't be
    // clobbered by this spread.
    setDomain((prev) => ({ ...prev, customEventConversions: derived }));
  };

  const updateRow = (action, mutate) => {
    const nextRows = rows.map((r) => (r.action === action ? mutate({ ...r, relevaTypes: [...r.relevaTypes] }) : r));
    // Keep a row visible while it's being reassembled: a mapping-less row is
    // not in the map and would otherwise vanish mid-edit.
    const edited = nextRows.find((r) => r.action === action);
    if (edited && !edited.relevaTypes.length && !edited.adType) {
      setDraftActions((drafts) => (drafts.includes(action) ? drafts : [...drafts, action]));
    }
    commitRows(nextRows);
  };

  const setRowRelevaTypes = (action, relevaTypes) => updateRow(action, (r) => ({ ...r, relevaTypes }));

  // Only real ad-conversion types are stored as the row's ad type; picking or
  // clearing back to the implied standard mapping (Purchase / Add to cart)
  // stores nothing extra — the row's commerce type already drives that
  // forwarding.
  const setRowAdType = (action, adType) =>
    updateRow(action, (r) => ({ ...r, adType: adType && AD_TYPE_VALUES.has(adType) ? adType : null }));

  const setRowValue = (action, value) => updateRow(action, (r) => ({ ...r, value }));

  const removeRow = (action) => {
    setDraftActions((drafts) => drafts.filter((a) => a !== action));
    commitRows(rows.filter((r) => r.action !== action));
  };

  const addRow = (action) => {
    if (action && !rows.some((r) => r.action === action)) setDraftActions((drafts) => [...drafts, action]);
  };

  const renderTypeOption = ({ option }) => {
    const meta =
      OPTION_META.find((e) => e.value === option.value && (!option.label || e.label === option.label)) ||
      OPTION_META.find((e) => e.value === option.value);
    return (
      <div>
        <Text size="sm">{option.label}</Text>
        {meta?.description && (
          <Text size="xs" c="dimmed">
            {meta.description}
          </Text>
        )}
      </div>
    );
  };

  const relevaTypeData = ANALYTICS_CONVERSION_EVENTS.map((e) => ({ value: e.value, label: e.label }));
  const adTypeData = CONVERSION_EVENTS.map((e) => ({ value: e.value, label: e.label }));

  const existingActions = new Set(rows.map((r) => r.action));
  const customActionPool = [...new Set(eventActions.map((a) => a.value))]
    .filter(
      (v) =>
        !existingActions.has(v) &&
        !UNMAPPABLE_ACTIONS.includes(v) &&
        !STANDARD_ACTIONS.includes(v) &&
        !v.startsWith('Catalog trigger - ')
    )
    .sort();
  const addPool = [
    {
      group: 'Standard events',
      items: STANDARD_ACTIONS.filter((v) => !existingActions.has(v)).map((v) => ({ value: v, label: v })),
    },
    { group: 'Custom events', items: customActionPool.map((v) => ({ value: v, label: v })) },
  ].filter((g) => g.items.length);

  const additionalAllowedEvents = domain.additionalAllowedTriggerEvents || [];

  const availableNotAllowedEventsForTrigger = notAllowedEventsForTrigger.filter(
    (event) => !additionalAllowedEvents.includes(event)
  );

  const getEventLabel = (eventValue) => {
    const eventType = eventTypes.find((e) => e.value === eventValue);

    if (eventType) {
      return eventType.label;
    }

    // Convert camelCase to "Camel Case"
    return eventValue
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Functional updates, unlike the object spread these were moved with: this tab
  // now also owns the conversions table, which commits the same domain the same
  // way, and a spread of the closed-over `domain` would drop a conversions edit
  // batched into the same render.
  const handleAddEvent = () => {
    if (!selectedEvent) return;

    setDomain((prev) => ({
      ...prev,
      additionalAllowedTriggerEvents: [...(prev.additionalAllowedTriggerEvents || []), selectedEvent],
    }));
    setSelectedEvent('');
  };

  const handleRemoveEvent = (eventToRemove) => {
    setDomain((prev) => ({
      ...prev,
      additionalAllowedTriggerEvents: (prev.additionalAllowedTriggerEvents || []).filter(
        (event) => event !== eventToRemove
      ),
    }));
  };

  return (
    <Card withBorder>
      <Card.Section p="md">
        <SuperAdminArea show={isSuperAdmin} mb="md">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Conversions</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              {rows.length > 0 && (
                <Group mb={4} gap="xs" wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                    Event
                  </Text>
                  <div style={{ width: 16, flexShrink: 0 }} />
                  <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                    Releva mapping
                  </Text>
                  <div style={{ width: 16, flexShrink: 0 }} />
                  <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                    Social media mapping
                  </Text>
                  <Text size="xs" c="dimmed" style={{ width: 120, flexShrink: 0 }}>
                    Value
                  </Text>
                  <div style={{ width: 28, flexShrink: 0 }} />
                </Group>
              )}
              {rows.map((row) => {
                const implied = row.relevaTypes.includes('Purchase')
                  ? IMPLIED_SOCIAL_MAPPINGS.Purchase
                  : row.relevaTypes.includes('AddToCart')
                    ? IMPLIED_SOCIAL_MAPPINGS.AddToCart
                    : null;
                const socialData = implied
                  ? [
                      {
                        group: 'Standard (from Releva mapping)',
                        items: [{ value: implied.value, label: implied.label }],
                      },
                      { group: 'Ad conversions', items: adTypeData },
                    ]
                  : adTypeData;
                return (
                  <Group key={row.action} mb="xs" gap="xs" align="center" wrap="nowrap">
                    <TextInput style={{ flex: 1 }} size="sm" value={row.action} aria-label="Event" disabled />
                    <IconArrowRight size={16} style={{ flexShrink: 0, opacity: 0.4 }} />
                    <MultiSelect
                      style={{ flex: 1 }}
                      size="sm"
                      data={relevaTypeData}
                      value={row.relevaTypes}
                      onChange={(relevaTypes) => setRowRelevaTypes(row.action, relevaTypes)}
                      renderOption={renderTypeOption}
                      placeholder={row.relevaTypes.length ? undefined : 'Not counted'}
                      aria-label="Releva mapping"
                      searchable
                      clearable
                    />
                    <IconArrowRight size={16} style={{ flexShrink: 0, opacity: 0.4 }} />
                    <Select
                      style={{ flex: 1 }}
                      size="sm"
                      data={socialData}
                      value={row.adType ?? implied?.value ?? null}
                      onChange={(adType) => setRowAdType(row.action, adType)}
                      renderOption={renderTypeOption}
                      placeholder="Not forwarded"
                      aria-label="Social media mapping"
                      disabled={!!implied?.locked}
                      searchable
                      clearable={!implied}
                    />
                    <NumberInput
                      w={120}
                      size="sm"
                      value={row.value ?? ''}
                      onChange={(value) =>
                        setRowValue(row.action, value === '' || value === undefined ? undefined : Number(value))
                      }
                      // A value is only persisted alongside a mapping (it becomes
                      // customEventConversions[action].value). Disable it until one is
                      // picked so a value can't be entered and then silently dropped.
                      disabled={!row.relevaTypes.length && !row.adType}
                      placeholder={row.relevaTypes.length || row.adType ? 'Value' : 'Map it first'}
                      aria-label="Conversion value"
                      rightSection={
                        domain.currency ? (
                          <Text size="xs" c="dimmed">
                            {domain.currency}
                          </Text>
                        ) : null
                      }
                      rightSectionWidth={42}
                      rightSectionPointerEvents="none"
                      min={0}
                      hideControls
                    />
                    <ActionIcon
                      color="red"
                      variant="light"
                      aria-label="Remove row"
                      onClick={() => removeRow(row.action)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                );
              })}

              <Select
                mt={rows.length ? 'xs' : 0}
                size="sm"
                data={addPool}
                value={null}
                onChange={(action) => addRow(action)}
                placeholder="Add a standard or custom event..."
                leftSection={<IconPlus size={16} />}
                disabled={!addPool.length}
                searchable
                nothingFoundMessage="No more events"
              />

              <Text size="xs" c="dimmed" mt="xs">
                Each event flows left to right: how Releva counts it in Reports &amp; Analytics, then the ad conversion
                it&apos;s forwarded as (enable it per platform in that platform&apos;s settings). The optional value
                {domain.currency ? ` (${domain.currency})` : ''} is used when the event carries no products/value of its
                own.
              </Text>
            </Grid.Col>
          </Grid>
        </SuperAdminArea>

        <SuperAdminArea show={isSuperAdmin} mb="md">
          {triggerEventsLoading ? (
            <Spinner />
          ) : triggerEventsError ? (
            <ErrorMessage message={triggerEventsError} />
          ) : (
            <>
              <Grid align="center">
                <Grid.Col span={{ base: 12, sm: 4 }} mb="md">
                  <Text fw={500}>Allow a High-Frequency Event</Text>
                  <Text size="sm" c="dimmed">
                    Select an event to allow to be selected as a trigger. High-frequency events are excluded from
                    trigger campaigns by default to prevent performance issues.
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    placeholder="Select an event to allow..."
                    value={selectedEvent}
                    onChange={setSelectedEvent}
                    data={availableNotAllowedEventsForTrigger.map((event) => ({
                      value: event,
                      label: getEventLabel(event),
                    }))}
                    searchable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 2 }}>
                  <Button onClick={handleAddEvent} disabled={!selectedEvent}>
                    <IconPlus size={14} /> Add
                  </Button>
                </Grid.Col>
              </Grid>

              <Grid align="flex-start">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Additional Allowed Trigger Events</Text>
                  <Text size="sm" c="dimmed">
                    These high-frequency events are allowed for trigger campaigns in this domain
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  {additionalAllowedEvents.length === 0 ? (
                    <Alert variant="light" color="gray">
                      No additional events are currently allowed. All high-frequency events are blocked for trigger
                      campaigns.
                    </Alert>
                  ) : (
                    <Table striped withTableBorder highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Event Name</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Action</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {additionalAllowedEvents.map((event) => (
                          <Table.Tr key={event}>
                            <Table.Td>
                              <Text>{getEventLabel(event)}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Button color="red" size="sm" onClick={() => handleRemoveEvent(event)}>
                                <IconTrash size={14} /> Remove
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Grid.Col>
              </Grid>
            </>
          )}
        </SuperAdminArea>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Campaign Attribution</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={domain.campaignAttribution ?? ''}
              onChange={(campaignAttribution) => setDomain({ ...domain, campaignAttribution })}
              data={[
                { label: 'Clicks', value: 'click' },
                { label: 'Opens & Clicks', value: 'openOrClick' },
              ]}
            />
            <Text size="xs" c="dimmed" mt="xs">
              This setting controls which user actions will be considered when attributing conversions and purchases to
              campaigns and marketing channels.
              <br />
              For example, if "Opens & Clicks" is selected, conversions and purchases will be attributed to Campaign A
              and email A (part of campaign A) up to 1 week after a user has opened email A or clicked on a link
              contained in email A.
            </Text>
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Custom URL Parameters</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            {/* List of existing parameters */}
            {Object.entries(domain.customUrlParameters || {}).map(([channel, params]) =>
              params.map((param, index) => (
                <Group key={`${channel}-${index}`} mb="xs" gap="xs">
                  <Select
                    style={{ width: 140 }}
                    size="sm"
                    data={CHANNEL_OPTIONS}
                    value={channel}
                    onChange={(newChannel) => {
                      if (!newChannel || newChannel === channel) return;
                      const updated = structuredClone(domain.customUrlParameters || {});
                      updated[channel] = updated[channel].filter((_, i) => i !== index);
                      if (updated[channel].length === 0) delete updated[channel];
                      if (!updated[newChannel]) updated[newChannel] = [];
                      updated[newChannel].push(param);
                      handleChange({ target: { name: 'customUrlParameters', value: updated } });
                    }}
                  />
                  <TextInput
                    style={{ flex: 1 }}
                    size="sm"
                    placeholder="Parameter name"
                    value={param.key || ''}
                    onChange={(e) => {
                      const updated = structuredClone(domain.customUrlParameters || {});
                      updated[channel][index] = { ...param, key: e.target.value };
                      handleChange({ target: { name: 'customUrlParameters', value: updated } });
                    }}
                  />
                  {channel === 'global' ? (
                    <TextInput
                      style={{ flex: 1 }}
                      size="sm"
                      placeholder="Value"
                      value={param.value || ''}
                      onChange={(e) => {
                        const updated = structuredClone(domain.customUrlParameters || {});
                        updated[channel][index] = { ...param, value: e.target.value };
                        handleChange({ target: { name: 'customUrlParameters', value: updated } });
                      }}
                    />
                  ) : (
                    <Autocomplete
                      style={{ flex: 1 }}
                      size="sm"
                      placeholder="Value (e.g. {{profileId}})"
                      data={AD_CHANNELS.includes(channel) ? AD_MERGE_TAG_OPTIONS : MERGE_TAG_OPTIONS}
                      value={param.value || ''}
                      onChange={(value) => {
                        const updated = structuredClone(domain.customUrlParameters || {});
                        updated[channel][index] = { ...param, value };
                        handleChange({ target: { name: 'customUrlParameters', value: updated } });
                      }}
                    />
                  )}
                  <ActionIcon
                    color="red"
                    variant="light"
                    onClick={() => {
                      const updated = structuredClone(domain.customUrlParameters || {});
                      updated[channel] = updated[channel].filter((_, i) => i !== index);
                      if (updated[channel].length === 0) delete updated[channel];
                      handleChange({ target: { name: 'customUrlParameters', value: updated } });
                    }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))
            )}

            <Button
              variant="light"
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                const updated = structuredClone(domain.customUrlParameters || {});
                if (!updated.global) updated.global = [];
                updated.global.push({ key: '', value: '' });
                handleChange({ target: { name: 'customUrlParameters', value: updated } });
              }}
            >
              Add Parameter
            </Button>
            <Text size="xs" c="dimmed" mt="xs">
              Add custom parameters to tracked URLs (e.g. utm_custom={'{{profileId}}'}). Parameters set for "All
              Channels" apply everywhere and only support static values.
            </Text>
          </Grid.Col>
        </Grid>

        {/* <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Logo</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <ImageInput
              file={domain.logoFile}
              setFile={(file) => handleChange({ target: { name: 'logoFile', value: file } })}
              imageUrl={domain.logoUrl}
              description="This logo is automatically downloaded by your domain name using logo.clearbit.com, it may be different
              from the logo that you use on your website."
            />
          </Grid.Col>
        </Grid> */}
      </Card.Section>
    </Card>
  );
};

export default SettingsTabAnalytics;
